"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Globe, Monitor, Smartphone, Tablet } from "lucide-react"
import ReactCountryFlag from "react-country-flag"
import { AnalyticsSummary } from "@/services/analytics-dashboard-service"
import { useTranslation } from "react-i18next"

interface AnalyticsTopListsProps {
  analytics: AnalyticsSummary
  className?: string
}

const CountryFlag = ({ countryCode }: { countryCode?: string }) => {
  if (!countryCode) {
    return (
      <div className="flex items-center justify-center w-6 h-4 bg-muted rounded-sm">
        <Globe className="h-3 w-3 text-muted-foreground" />
      </div>
    )
  }

  return (
    <ReactCountryFlag
      countryCode={countryCode.toUpperCase()}
      svg
      style={{
        width: "1.5rem",
        height: "1rem",
        borderRadius: "2px",
      }}
      title={countryCode.toUpperCase()}
    />
  )
}

const DeviceIcon = ({ deviceType }: { deviceType?: string }) => {
  const iconClass = "h-4 w-4"
  const { t } = useTranslation("analytics")
  switch (deviceType?.toLowerCase()) {
    case t("analytics:topList.mobile"):
      return <Smartphone className={`${iconClass} text-blue-600`} />
    case t("analytics:topList.desktop"):
      return <Tablet className={`${iconClass} text-green-600`} />
    case t("analytics:topList.tablet"):
      return <Monitor className={`${iconClass} text-purple-600`} />
    default:
      return <Monitor className={`${iconClass} text-muted-foreground`} />
  }
}

export function AnalyticsTopLists({ analytics, className }: AnalyticsTopListsProps) {
  const { t } = useTranslation("analytics")
  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${className}`}>
      {/* Top Countries */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Globe className="h-4 w-4" />
            {t("analytics:topList.topCountries")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {analytics.top_countries && analytics.top_countries.length > 0 ? (
            analytics.top_countries.slice(0, 5).map((country, index) => (
              <div key={index} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <CountryFlag countryCode={country.country} />
                  <span className="text-sm font-medium truncate">{country.country || "Unknown"}</span>
                </div>
                <Badge variant="secondary" className="ml-2 shrink-0">
                  {country.count}
                </Badge>
              </div>
            ))
          ) : (
            <div className="text-center py-6">
              <Globe className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{t("analytics:topList.noCountry")}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Devices */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Monitor className="h-4 w-4" />
            {t("analytics:topList.deviceType")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {analytics.top_devices && analytics.top_devices.length > 0 ? (
            analytics.top_devices.map((device, index) => (
              <div key={index} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <DeviceIcon deviceType={device.device} />
                  <span className="text-sm font-medium capitalize truncate">{device.device || "Unknown"}</span>
                </div>
                <Badge variant="secondary" className="ml-2 shrink-0">
                  {device.count}
                </Badge>
              </div>
            ))
          ) : (
            <div className="text-center py-6">
              <Monitor className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{t("analytics:topList.noDevice")}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
